'use client';

import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  lazy,
  Suspense,
} from 'react';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { useGetEntityPlaysQuery } from 'src/api/redux/profileConfiguration';
import type { MatchedPlay } from 'src/api/redux/profileConfiguration/types';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { PulseIconButton } from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import { usePlayNavigation } from 'src/context/providers/PlayNavigationProvider';
import { PlaysCard } from './components';

const PlaysDetailPanel = lazy(() => import('./components/PlaysDetailPanel'));

const SCROLL_AMOUNT = 300;

const PlaysSectionV2 = () => {
  const { entityDetails } = useProfile();
  const navigateToPlay = usePlayNavigation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [selectedPlay, setSelectedPlay] = useState<MatchedPlay | null>(null);

  const { data, isLoading } = useGetEntityPlaysQuery(
    { entityId: entityDetails?.id },
    { skip: !entityDetails?.id }
  );

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [updateScrollState, data]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT,
      behavior: 'smooth',
    });
  };

  const dragState = useRef({
    isDown: false,
    didDrag: false,
    startX: 0,
    scrollLeft: 0,
  });

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    dragState.current = {
      isDown: true,
      didDrag: false,
      startX: e.pageX - el.offsetLeft,
      scrollLeft: el.scrollLeft,
    };
    el.style.cursor = 'grabbing';
    el.style.userSelect = 'none';
    el.style.scrollSnapType = 'none';
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragState.current.isDown) return;
    dragState.current.didDrag = true;
    const el = scrollRef.current;
    if (!el) return;
    const x = e.pageX - el.offsetLeft;
    el.scrollLeft =
      dragState.current.scrollLeft - (x - dragState.current.startX);
  }, []);

  const handleMouseEnd = useCallback(() => {
    dragState.current.isDown = false;
    const el = scrollRef.current;
    if (el) {
      el.style.cursor = 'grab';
      el.style.userSelect = '';
      el.style.scrollSnapType = '';
    }
  }, []);

  const handleCardClick = useCallback(
    (play: MatchedPlay) => {
      if (dragState.current.didDrag) return;
      if (navigateToPlay) {
        navigateToPlay(play);
      } else {
        setSelectedPlay(play);
      }
    },
    [navigateToPlay]
  );

  const count = data?.results?.length ?? 0;

  if (isLoading) {
    return (
      <WidgetLoading data-testid="plays-section-v2-loading" className="flex items-center justify-center py-8" />
    );
  }

  if (!count) return null;

  return (
    <div data-testid="plays-section-v2">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-neutral-800">Plays</h3>
            <span
              className="flex items-center justify-center w-6 h-6 rounded-full bg-sky-600 text-white text-xs font-semibold"
              data-testid="plays-section-v2-count"
              aria-label={`${count} plays`}
            >
              {count}
            </span>
          </div>
          {count > 1 && (
            <div className="flex items-center gap-1">
              <PulseIconButton
                variant="outline"
                color="neutral"
                size="sm"
                icon={faChevronLeft}
                onClick={() => scroll('left')}
                disabled={!canScrollLeft}
                aria-label="Scroll plays left"
                data-testid="plays-section-v2-scroll-left"
                className="focus:ring-0 focus:ring-offset-0"
              />
              <PulseIconButton
                variant="outline"
                color="neutral"
                size="sm"
                icon={faChevronRight}
                onClick={() => scroll('right')}
                disabled={!canScrollRight}
                aria-label="Scroll plays right"
                data-testid="plays-section-v2-scroll-right"
                className="focus:ring-0 focus:ring-offset-0"
              />
            </div>
          )}
        </div>
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory cursor-grab py-2 -my-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseEnd}
          onMouseLeave={handleMouseEnd}
        >
          {data?.results?.map((play) => (
            <PlaysCard
              key={play.id}
              play={play}
              onClick={() => handleCardClick(play)}
            />
          ))}
        </div>
      </div>

      {!navigateToPlay && selectedPlay && (
        <Suspense fallback={null}>
          <PlaysDetailPanel
            play={selectedPlay}
            onClose={() => setSelectedPlay(null)}
          />
        </Suspense>
      )}
    </div>
  );
};

export default PlaysSectionV2;
